import React, { useState, useEffect, useCallback } from "react";
import {
  Filter,
  Heart,
  Star,
  X,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  ChevronRight,
  ChevronLeft,
  Home,
  Menu,
  SlidersHorizontal,
} from "lucide-react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { useNavigate } from "react-router-dom";

const Luggage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    categories: [],
    colors: [],
    sizes: [],
    priceRange: { min: 0, max: 100000000 },
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 12,
  });

  // ALL FILTERS IN ONE STATE
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    search: "",
    category: "",
    productType: "luggage", // Add productType filter for luggage
    priceMin: "",
    priceMax: "",
    sizes: [],
    colors: [],
    sortBy: "newest",
    inStock: true,
    saleOnly: false,
    brand: [],
    minRating: 0,
  });

  // APPLIED FILTERS - ONLY THESE TRIGGER API CALLS
  const [appliedFilters, setAppliedFilters] = useState({
    page: 1,
    limit: 12,
    search: "",
    category: "",
    productType: "luggage", // Add productType filter for luggage
    priceMin: "",
    priceMax: "",
    sizes: [],
    colors: [],
    sortBy: "newest",
    inStock: true,
    saleOnly: false,
    brand: [],
    minRating: 0,
  });

  const [expandedSections, setExpandedSections] = useState({
    availability: true,
    price: true,
    color: true,
    size: true,
    brand: true,
    rating: true,
    category: true,
  });

  // Check screen size and update isMobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-close filters on desktop if they were open in mobile view
      if (window.innerWidth >= 768 && showMobileFilters) {
        setShowMobileFilters(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showMobileFilters]);

  // Fetch filter options
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch products when APPLIED filters change
  useEffect(() => {
    fetchProducts();
  }, [appliedFilters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getProductFilters}?productType=luggage`,
      });

      if (response.success) {
        let priceRange = { min: 0, max: 1000 };
        if (
          response.data.priceRange &&
          Array.isArray(response.data.priceRange) &&
          response.data.priceRange.length > 0
        ) {
          priceRange = response.data.priceRange[0];
        }

        setFilterOptions({
          brands: response.data.brands || [],
          categories: response.data.categories || [],
          colors: response.data.colors || [],
          sizes: response.data.sizes || [],
          priceRange,
        });
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          if (Array.isArray(value)) {
            params.append(key, value.join(","));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getProducts}?${params.toString()}`,
      });

      if (response.success) {
        setProducts(response.data.products || response.data || []);
        setPagination(
          response.data.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalProducts: (response.data.products || response.data || []).length,
            hasNextPage: false,
            hasPrevPage: false,
            limit: 12,
          }
        );
      } else {
        setError("Failed to fetch products");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  const updateFilterAndApply = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  };

  const toggleArrayFilterAndApply = (key, value) => {
    const isSelected = filters[key].includes(value);
    const newArray = isSelected
      ? filters[key].filter((item) => item !== value)
      : [...filters[key], value];

    const newFilters = { ...filters, [key]: newArray, page: 1 };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      const newFilters = { ...filters, page: newPage };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
      
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (pagination.hasPrevPage) {
      handlePageChange(pagination.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      handlePageChange(pagination.currentPage + 1);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const currentPage = pagination.currentPage;
    const totalPages = pagination.totalPages;
    
    if (totalPages <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination
      if (currentPage <= 4) {
        // Show first 5 pages + ... + last page
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show first page + ... + last 5 pages
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first + ... + current-1, current, current+1 + ... + last
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const resetFilters = () => {
    const resetState = {
      page: 1,
      limit: 12,
      search: "",
      category: "",
      productType: "luggage", // Keep luggage productType
      priceMin: "",
      priceMax: "",
      sizes: [],
      colors: [],
      sortBy: "newest",
      inStock: false,
      saleOnly: false,
      brand: [],
      minRating: 0,
    };
    setFilters(resetState);
    setAppliedFilters(resetState);

    // Reset price inputs
    const priceMinInput = document.getElementById("price-min");
    const priceMaxInput = document.getElementById("price-max");
    if (priceMinInput) priceMinInput.value = "";
    if (priceMaxInput) priceMaxInput.value = "";
  };

  const handlePriceChange = () => {
    const priceMinInput = document.getElementById("price-min");
    const priceMaxInput = document.getElementById("price-max");
    const priceMin = priceMinInput?.value || "";
    const priceMax = priceMaxInput?.value || "";

    const newFilters = {
      ...filters,
      priceMin,
      priceMax,
      page: 1,
    };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Color mapping
  const colorMap = {
    Red: "bg-red-400",
    Blue: "bg-blue-400",
    Black: "bg-gray-900",
    White: "bg-gray-100",
    Green: "bg-green-400",
    Yellow: "bg-yellow-400",
    Orange: "bg-orange-400",
    Purple: "bg-purple-400",
    Pink: "bg-pink-400",
    Brown: "bg-yellow-800",
    Gray: "bg-gray-400",
    Navy: "bg-blue-900",
    Silver: "bg-gray-300",
  };

  const FilterSection = ({ title, children, sectionKey }) => (
    <div className="mb-4 border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
      <button
        className="flex items-center justify-between w-full text-left"
        onClick={() => toggleSection(sectionKey)}
      >
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        {expandedSections[sectionKey] ? (
          <ChevronUp size={16} className="text-gray-500" />
        ) : (
          <ChevronDown size={16} className="text-gray-500" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="mt-2 animate-fadeIn">{children}</div>
      )}
    </div>
  );

  const ProductCard = ({ product }) => {
    const [selectedImage, setSelectedImage] = useState(
      product.mainImage || "/placeholder-product.jpg"
    );
    const [selectedColorIndex, setSelectedColorIndex] = useState(0);

    const handleColorClick = (colorOption, index) => {
      if (colorOption.image) {
        setSelectedImage(colorOption.image);
        setSelectedColorIndex(index);
      }
    };

    useEffect(() => {
      setSelectedImage(product.mainImage || "/placeholder-product.jpg");
      setSelectedColorIndex(0);
    }, [product.mainImage, product._id]);

    return (
      <div className="group relative bg-white shadow-sm border border-gray-200 overflow-hidden cursor-pointer transform transition-all duration-300 hover:shadow-lg md:hover:scale-[1.02]">
        {product.onSale && (
          <div className="absolute top-3 left-3 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full z-10">
            SALE
          </div>
        )}

        <div
          onClick={() => navigate(`/product/${product._id}`)}
          className="relative aspect-w-1 aspect-h-1 overflow-hidden"
        >
          <img
            src={selectedImage}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 md:group-hover:scale-105"
            onError={(e) => {
              e.target.src = "/placeholder-product.jpg";
            }}
          />
          <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {/* Optional: Add wishlist button if needed */}
          </div>
        </div>

        <div
          className="p-4 md:p-5"
          onClick={() => navigate(`/product/${product._id}`)}
        >
          <h3 className="text-sm md:text-base font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">
            {product.name}
          </h3>

          <div className="flex items-center mb-2">
            <span className="text-base md:text-lg text-gray-900">
              ₹{product.minPrice?.toLocaleString() || "N/A"}
            </span>
            {product.maxPrice && product.maxPrice > product.minPrice && (
              <span className="text-xs md:text-sm text-gray-500 ml-2 line-through">
                ₹{product.maxPrice?.toLocaleString()}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {product.colorOptions?.slice(0, 4).map((colorOption, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  handleColorClick(colorOption, index);
                }}
                className={`w-4 h-4 md:w-6 md:h-6 rounded-full border-2 transition-all duration-200 shadow-sm
                                  ${colorMap[colorOption.name] || "bg-gray-300"}
                                  ${
                                    selectedColorIndex === index
                                      ? "border-blue-500 ring-2 ring-blue-500"
                                      : "border-gray-200 hover:border-gray-400"
                                  }`}
                title={colorOption.name}
              />
            ))}
            {product.colorOptions?.length > 4 && (
              <span className="text-xs md:text-sm text-gray-500">
                +{product.colorOptions.length - 4}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Pagination Component
  const PaginationComponent = () => {
    if (pagination.totalPages <= 1) return null;

    const pageNumbers = getPageNumbers();

    return (
      <div className="flex flex-col items-center space-y-4 mt-8 mb-8">
        {/* Results info */}
        <div className="text-sm text-gray-600">
          Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalProducts)} of {pagination.totalProducts} results
        </div>

        {/* Pagination controls */}
        <div className="flex items-center space-x-1">
          {/* Previous button */}
          <button
            onClick={handlePrevPage}
            disabled={!pagination.hasPrevPage}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              pagination.hasPrevPage
                ? "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                : "bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </button>

          {/* Page numbers */}
          <div className="hidden sm:flex items-center space-x-1">
            {pageNumbers.map((pageNum, index) => (
              pageNum === '...' ? (
                <span key={index} className="px-3 py-2 text-gray-500">...</span>
              ) : (
                <button
                  key={index}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    pageNum === pagination.currentPage
                      ? "bg-blue-600 text-white"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              )
            ))}
          </div>

          {/* Mobile page indicator */}
          <div className="sm:hidden px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md">
            {pagination.currentPage} of {pagination.totalPages}
          </div>

          {/* Next button */}
          <button
            onClick={handleNextPage}
            disabled={!pagination.hasNextPage}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              pagination.hasNextPage
                ? "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                : "bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            Next
            <ChevronRight size={16} className="ml-1" />
          </button>
        </div>
      </div>
    );
  };

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans antialiased mb-10">
      {/* Mobile Filter Overlay */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-end">
          <div className="bg-white w-4/5 max-w-sm h-full overflow-y-auto p-4 animate-slideInRight">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">FILTERS</h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <FilterSection title="AVAILABILITY" sectionKey="availability">
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.inStock}
                      onChange={(e) =>
                        updateFilterAndApply("inStock", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">In Stock</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.saleOnly}
                      onChange={(e) =>
                        updateFilterAndApply("saleOnly", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">On Sale</span>
                  </label>
                </div>
              </FilterSection>

              <FilterSection title="CATEGORY" sectionKey="category">
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {filterOptions.categories.map((category) => (
                    <label
                      key={category}
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.category === category}
                        onChange={() =>
                          updateFilterAndApply("category", category)
                        }
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{category}</span>
                    </label>
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="COLOR" sectionKey="color">
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {filterOptions.colors.map((color) => (
                    <label
                      key={color}
                      className="flex items-center cursor-pointer py-1"
                    >
                      <input
                        type="checkbox"
                        checked={filters.colors.includes(color)}
                        onChange={() =>
                          toggleArrayFilterAndApply("colors", color)
                        }
                        className="mr-2"
                      />
                      <div
                        className={`w-4 h-4 rounded-full ${
                          colorMap[color] || "bg-gray-300"
                        } mr-2 border`}
                      ></div>
                      <span className="text-sm text-gray-700">{color}</span>
                    </label>
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="BRAND" sectionKey="brand">
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {filterOptions.brands.map((brand) => (
                    <label
                      key={brand}
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.brand.includes(brand)}
                        onChange={() =>
                          toggleArrayFilterAndApply("brand", brand)
                        }
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{brand}</span>
                    </label>
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="PRICE" sectionKey="price">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      id="price-min"
                      onBlur={handlePriceChange}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-gray-500">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      id="price-max"
                      onBlur={handlePriceChange}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
              </FilterSection>
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex space-x-4">
              <button
                onClick={resetFilters}
                className="flex-1 py-3 border border-gray-300 rounded-md font-medium"
              >
                Reset
              </button>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 py-3 bg-black text-white rounded-md font-medium"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner Section */}
      <div
        className="relative w-full h-[150px] sm:h-[200px] md:h-[250px] mb-3 bg-cover bg-center overflow-hidden"
        style={{
          backgroundImage: `url(${require("../../assets/images/home/img_bag.jpg")})`,
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-white text-center p-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 uppercase tracking-wide">
            Luggage
          </h2>
          <p className="text-sm sm:text-base md:text-lg max-w-2xl font-light leading-relaxed">
            Discover the latest additions to our collection of stylish and
            functional bags.
          </p>
        </div>
      </div>
      <div className="mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Filter and Sort bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 pb-2 border-b border-gray-200 gap-2">
          <div className="flex items-center space-x-4">
            {isMobile ? (
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center font-semibold text-gray-700 hover:text-gray-900"
              >
                <SlidersHorizontal size={16} className="mr-2" />
                FILTER & SORT
              </button>
            ) : (
              <>
                <button
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className="flex items-center font-semibold text-gray-700 hover:text-gray-900"
                >
                  <Filter size={16} className="mr-2" />
                  FILTER
                </button>
                <div className="w-px h-6 bg-gray-300" />
                <div className="relative group">
                  <button className="flex items-center font-semibold text-gray-700 hover:text-gray-900">
                    SORT BY:{" "}
                    <span className="ml-1 font-normal capitalize">
                      {appliedFilters.sortBy}
                    </span>
                    <ChevronDown size={16} className="ml-1" />
                  </button>
                  <div className="absolute left-0 top-full mt-2 w-48 bg-white border border-gray-200 shadow-lg rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20">
                    <ul className="py-2">
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => updateFilterAndApply("sortBy", "newest")}
                      >
                        Newest
                      </li>
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() =>
                          updateFilterAndApply("sortBy", "priceAsc")
                        }
                      >
                        Price: Low to High
                      </li>
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() =>
                          updateFilterAndApply("sortBy", "priceDesc")
                        }
                      >
                        Price: High to Low
                      </li>
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => updateFilterAndApply("sortBy", "rating")}
                      >
                        Customer Rating
                      </li>
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() =>
                          updateFilterAndApply("sortBy", "popular")
                        }
                      >
                        Most Popular
                      </li>
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() =>
                          updateFilterAndApply("sortBy", "nameAsc")
                        }
                      >
                        Name: A to Z
                      </li>
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() =>
                          updateFilterAndApply("sortBy", "nameDesc")
                        }
                      >
                        Name: Z to A
                      </li>
                    </ul>
                  </div>
                </div>
              </>
            )}
          </div>
          <span className="text-sm text-gray-600 hidden sm:block">
            Showing {pagination.totalProducts} of {pagination.totalProducts}{" "}
            products
          </span>
        </div>

        <div className="flex gap-4 md:gap-8">
          {/* Desktop Filter Sidebar */}
          {filtersOpen && !isMobile && (
            <aside className="w-1/4 max-w-[250px] transition-all duration-300 hidden md:block">
              <div className="p-4 bg-white rounded-lg sticky top-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">FILTERS</h2>
                  <button
                    onClick={resetFilters}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Clear All
                  </button>
                </div>
                <FilterSection title="AVAILABILITY" sectionKey="availability">
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.inStock}
                        onChange={(e) =>
                          updateFilterAndApply("inStock", e.target.checked)
                        }
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">In Stock</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.saleOnly}
                        onChange={(e) =>
                          updateFilterAndApply("saleOnly", e.target.checked)
                        }
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">On Sale</span>
                    </label>
                  </div>
                </FilterSection>
                <FilterSection title="CATEGORY" sectionKey="category">
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filterOptions.categories.map((category) => (
                      <label
                        key={category}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.category === category}
                          onChange={() =>
                            updateFilterAndApply("category", category)
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">
                          {category}
                        </span>
                      </label>
                    ))}
                  </div>
                </FilterSection>
                <FilterSection title="COLOR" sectionKey="color">
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {filterOptions.colors.map((color) => (
                      <label
                        key={color}
                        className="flex items-center cursor-pointer py-1"
                      >
                        <input
                          type="checkbox"
                          checked={filters.colors.includes(color)}
                          onChange={() =>
                            toggleArrayFilterAndApply("colors", color)
                          }
                          className="mr-2"
                        />
                        <div
                          className={`w-4 h-4 rounded-full ${
                            colorMap[color] || "bg-gray-300"
                          } mr-2 border`}
                        ></div>
                        <span className="text-sm text-gray-700">{color}</span>
                      </label>
                    ))}
                  </div>
                </FilterSection>
                <FilterSection title="BRAND" sectionKey="brand">
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {filterOptions.brands.map((brand) => (
                      <label
                        key={brand}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.brand.includes(brand)}
                          onChange={() =>
                            toggleArrayFilterAndApply("brand", brand)
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{brand}</span>
                      </label>
                    ))}
                  </div>
                </FilterSection>
                <FilterSection title="PRICE" sectionKey="price">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        id="price-min"
                        onBlur={handlePriceChange}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      <span className="text-gray-500">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        id="price-max"
                        onBlur={handlePriceChange}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                </FilterSection>
              </div>
            </aside>
          )}

          <main
            className={
              filtersOpen && !isMobile
                ? "w-3/4 transition-all duration-300"
                : "w-full transition-all duration-300"
            }
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
                {error}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-gray-100 rounded-lg shadow-sm animate-pulse overflow-hidden"
                  >
                    <div className="aspect-square bg-gray-200"></div>
                    <div className="p-4 md:p-5 space-y-3">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <>
                <div
                  className={
                    filtersOpen && !isMobile
                      ? "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6"
                      : "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 md:gap-6"
                  }
                >
                  {products.map((product) => (
                    <ProductCard key={product._id} product={product} />
                  ))}
                </div>
                
                {/* Pagination Component */}
                <PaginationComponent />
              </>
            ) : (
              <div className="text-center py-12 md:py-20 bg-white rounded-lg shadow-sm border border-gray-200">
                <p className="text-gray-500 text-base md:text-lg mb-4">
                  No products found matching your criteria.
                </p>
                <button
                  onClick={resetFilters}
                  className="bg-blue-600 text-white font-medium px-5 py-2 md:px-6 md:py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Luggage;